import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  MenuItem,
  Select,
  CircularProgress,
  TextField,
  Button,
  Modal,
  Avatar,
  TablePagination,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import MovieIcon from "@mui/icons-material/Movie";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import toast from "react-hot-toast";
import DaterangePicker from "../../common/Daterangepicker";
import formatDateAndTime from "../../common/formatDateAndTime";
import getactions from "../../common/getactions";

const FONT = "Outfit";
const PAGE_SIZE = 10;

// Helper to determine media type
const getMediaType = (url = "") => {
  const cleanUrl = url.split("?")[0].toLowerCase();
  if (/\.(mp4|webm|ogg|mov|mkv)$/i.test(cleanUrl)) return "video";
  if (/\.(mp3|wav|ogg|m4a|aac)$/i.test(cleanUrl)) return "audio";
  if (/\.(pdf)$/i.test(cleanUrl)) return "pdf";
  return "image";
};

// Date helper: Standardized YYYY-MM-DD HH:mm:ss format
const formatDateParam = (date, isEnd = false) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time = isEnd ? "23:59:59" : "00:00:00";
  return `${year}-${month}-${day} ${time}`;
};

const StatCard = ({ label, value, badge, badgeColor, badgeBg, sub, barColor }) => (
  <Box
    sx={{
      flex: 1,
      p: 3,
      position: "relative",
      bgcolor: "white",
      borderRadius: "16px",
      outline: "1px solid #E5E7EB",
      outlineOffset: "-1px",
      overflow: "hidden",
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Typography sx={{ color: "#6B7280", fontSize: 14, fontFamily: FONT, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ px: 1, py: 0.5, bgcolor: badgeBg, borderRadius: "6px" }}>
        <Typography
          sx={{
            color: badgeColor,
            fontSize: 11,
            fontFamily: FONT,
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {badge}
        </Typography>
      </Box>
    </Stack>
    <Typography
      sx={{
        color: "#111827",
        fontSize: 30,
        fontFamily: FONT,
        fontWeight: 700,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ color: "#9CA3AF", fontSize: 12, fontFamily: FONT, mt: 0.5 }}>
        {sub}
      </Typography>
    )}
    <Box
      sx={{
        width: 84,
        height: 10,
        position: "absolute",
        right: 24,
        top: 0,
        bgcolor: barColor,
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
      }}
    />
  </Box>
);

const FilterPill = ({ label, value, options, onChange }) => (
  <Box
    sx={{
      flex: 1,
      px: 1.75,
      py: 1.25,
      bgcolor: "white",
      borderRadius: "8px",
      outline: "1px solid #E5E7EB",
      outlineOffset: "-1px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      position: "relative",
    }}
  >
    <Box>
      <Typography
        sx={{
          color: "#9CA3AF",
          fontSize: 11,
          fontFamily: FONT,
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#1F2937",
          fontSize: 14,
          fontFamily: FONT,
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "160px",
        }}
      >
        {value}
      </Typography>
    </Box>
    <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#4B5563" }} />
    <Select
      value=""
      onChange={onChange}
      displayEmpty
      variant="standard"
      disableUnderline
      IconComponent={() => null}
      sx={{
        position: "absolute",
        inset: 0,
        opacity: 0,
        "& .MuiSelect-select": { height: "100%" },
      }}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.name}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

const StatusBadge = ({ row }) => {
  const isRejected = row.reviewStatus === "Rejected" || row.isRejected;
  const isVerified = row.isVerified && !isRejected;

  let label = "Pending";
  let color = "#D97706";
  let bg = "#FFF7ED";

  if (isRejected) {
    label = "Rejected";
    color = "#DC2626";
    bg = "#FEF2F2";
  } else if (isVerified) {
    label = "Verified";
    color = "#059669";
    bg = "#ECFDF5";
  } else if (row.reviewStatus == null) {
    label = "Pending (N/A)";
  }

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        bgcolor: bg,
        borderRadius: "99px",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Box sx={{ width: 6, height: 6, bgcolor: color, borderRadius: "9999px" }} />
      <Typography sx={{ color, fontSize: 13, fontFamily: FONT, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
};

const CustomerReviews = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const actionsToAccess = getactions(profile?.roles, profile?.actions);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pagination states (0-indexed page for MUI TablePagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter input states
  const [executiveOptions, setExecutiveOptions] = useState([]);
  const [inputExecutiveId, setInputExecutiveId] = useState("");
  const [inputVerificationStatus, setInputVerificationStatus] = useState("");
  const [inputBookingId, setInputBookingId] = useState("");

  // Verification & Status modal states
  const [statusModalConfig, setStatusModalConfig] = useState({
    open: false,
    item: null,
    actionType: "verify",
  });
  const [processingStatus, setProcessingStatus] = useState(false);

  // Media preview modal state
  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);

  // Date range picker state (Default: last 30 days)
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    key: "selection",
  });

  // Applied parameters
  const [appliedFilters, setAppliedFilters] = useState({
    fleetExecutiveId: "",
    status: "",
    bookingCode: "",
    startDate: formatDateParam(new Date(new Date().setDate(new Date().getDate() - 30)), false),
    endDate: formatDateParam(new Date(), true),
  });

  useEffect(() => {
    const fetchExecutives = async () => {
      try {
        const userRes = await fetch(
          `${apiUrl}/api/users?limit=1000&isSystemUser=1&isAgent=true`,
          {
            method: "GET",
            headers: { Authorization: `${token}` },
          }
        );
        const userData = await userRes.json();
        const userRows = userData?.dataValues?.rows || userData?.entities?.rows || [];
        if (userData?.statusCode === "0100" || userRes.ok) {
          setExecutiveOptions(
            userRows.map((u) => ({ id: String(u.id), name: u.name }))
          );
        }
      } catch (err) {
        console.error("Error fetching executives:", err);
      }
    };
    fetchExecutives();
  }, [apiUrl, token]);

const fetchCustomerReviews = async (filters, currentPage, limit) => {
  setLoading(true);
  try {
    const params = new URLSearchParams();
    if (filters.fleetExecutiveId) params.append("empId", filters.fleetExecutiveId);

    if (filters.status === "verified") {
      params.append("reviewStatus", "Approved");
    } else if (filters.status === "rejected") {
      params.append("reviewStatus", "Rejected");
    } else if (filters.status === "pending") {
      params.append("reviewStatus", "Pending");
    }

    if (filters.bookingCode) params.append("bookingCode", filters.bookingCode);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);

    params.append("limit", String(limit));
    params.append("offset", String(currentPage * limit));
    params.append("page", String(currentPage + 1));

      const response = await fetch(
        `${apiUrl}/api/customerreviews/fetch-details-with-search?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }
      );

      const data = await response.json();
      if (data?.statusCode === "0100" || response.ok) {
        const resultData = data?.dataValues || data?.entities || {};
        setRows(resultData?.rows || []);
        setTotalCount(resultData?.count || 0);
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching customer reviews:", error);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerReviews(appliedFilters, page, rowsPerPage);
  }, [appliedFilters, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenStatusModal = (item, actionType = "verify") => {
    setStatusModalConfig({ open: true, item, actionType });
  };

  const handleCloseStatusModal = () => {
    setStatusModalConfig({ open: false, item: null, actionType: "verify" });
  };

  const handleConfirmStatusChange = async () => {
    const { item, actionType } = statusModalConfig;
    if (!item) return;
    setProcessingStatus(true);
    try {
      const payload = {
        id: item.id,
        isVerified: actionType === "verify",
        reviewStatus: actionType === "verify" ? "Approved" : "Rejected",
        bookingId: item.bookingId,
      };

      const response = await fetch(`${apiUrl}/api/customerreviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data?.statusCode === "0100" || response.ok) {
        toast.success(
          actionType === "verify" ? "Review verified successfully!" : "Review marked as rejected!",
          { position: "top-center" }
        );
        handleCloseStatusModal();
        await fetchCustomerReviews(appliedFilters, page, rowsPerPage);
      } else {
        toast.error(`Failed to update customer review status`);
      }
    } catch (error) {
      console.error("Error updating review status:", error);
      toast.error("Error during status update process");
    } finally {
      setProcessingStatus(false);
    }
  };

  const handleSearchClick = () => {
    setPage(0);
    setAppliedFilters({
      fleetExecutiveId: inputExecutiveId,
      status: inputVerificationStatus,
      bookingCode: inputBookingId ? inputBookingId.trim() : "",
      startDate: selectionRange?.startDate ? formatDateParam(selectionRange.startDate, false) : "",
      endDate: selectionRange?.endDate ? formatDateParam(selectionRange.endDate, true) : "",
    });
  };

  const handleResetClick = () => {
  setInputExecutiveId("");
  setInputVerificationStatus("");
  setInputBookingId("");

  const defaultStart = new Date(new Date().setDate(new Date().getDate() - 30));
  const defaultEnd = new Date();

  setSelectionRange({ startDate: defaultStart, endDate: defaultEnd, key: "selection" });
  setPage(0);
  setAppliedFilters({
    fleetExecutiveId: "",
    status: "",
    bookingCode: "",
    startDate: formatDateParam(defaultStart, false),
    endDate: formatDateParam(defaultEnd, true),
  });
};

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (appliedFilters.fleetExecutiveId) params.append("empId", appliedFilters.fleetExecutiveId);
      if (appliedFilters.status === "verified") params.append("reviewStatus", "Approved");
      if (appliedFilters.status === "rejected") params.append("reviewStatus", "Rejected");
      if (appliedFilters.status === "pending") params.append("reviewStatus", "Pending");
      if (appliedFilters.bookingCode) params.append("bookingCode", appliedFilters.bookingCode);
      if (appliedFilters.startDate) params.append("startDate", appliedFilters.startDate);
      if (appliedFilters.endDate) params.append("endDate", appliedFilters.endDate);
      params.append("limit", "5000");

      const response = await fetch(
        `${apiUrl}/api/customerreviews/download-report?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }
      );

      const data = await response.json();
      const fileUrl = data?.dataValues?.Location || data?.Location;
      if ((data?.statusCode === "0100" || response.ok) && fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.setAttribute("download", "customer_reviews.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("Customer reviews exported successfully!", { position: "top-center" });
      } else {
        toast.error("Failed to generate download report");
      }
    } catch (error) {
      console.error("Error exporting reviews:", error);
      toast.error("An error occurred while exporting customer reviews.");
    }
  };

  const normalizeAttachments = (attachmentData) => {
    if (!attachmentData) return [];
    if (Array.isArray(attachmentData)) return attachmentData;
    return [attachmentData];
  };

  const renderThumbnailIcon = (url) => {
    const type = getMediaType(url);
    if (type === "video") return <MovieIcon sx={{ color: "#4F46E5", fontSize: 22 }} />;
    if (type === "audio") return <AudiotrackIcon sx={{ color: "#059669", fontSize: 22 }} />;
    if (type === "pdf") return <PictureAsPdfIcon sx={{ color: "#DC2626", fontSize: 22 }} />;
    return <InsertPhotoIcon sx={{ color: "#9CA3AF", fontSize: 22 }} />;
  };

  const columns = [
    { key: "executive", label: "Executive / Creator", flex: 1.1 },
    { key: "review", label: "Review Content", flex: 1.8 },
    { key: "media", label: "Attachment", width: 130 },
    { key: "status", label: "Status", width: 130 },
    { key: "created", label: "Date Added", width: 160 },
    { key: "action", label: "Action", width: 180, align: "right" },
  ];

  const verificationStatusOptions = [
    { id: "verified", name: "Verified" },
    { id: "pending", name: "Pending" },
    { id: "rejected", name: "Rejected" },
  ];

  const selectedExecutiveName =
    executiveOptions.find((e) => e.id === inputExecutiveId)?.name || "All Staff";
  const selectedStatusName =
    verificationStatusOptions.find((s) => s.id === inputVerificationStatus)?.name || "All Statuses";

  return (
    <>
      {/* Verify / Reject Modal */}
      {statusModalConfig.open && (
        <Modal open={statusModalConfig.open} onClose={handleCloseStatusModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 420,
              bgcolor: "background.paper",
              borderRadius: "16px",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mb: 1, fontFamily: FONT }}>
              {statusModalConfig.actionType === "verify" ? "Verify Customer Review" : "Reject Customer Review"}
            </Typography>
            <Typography sx={{ color: "#4B5563", fontSize: 14, mb: 3, fontFamily: FONT }}>
              Are you sure you want to {statusModalConfig.actionType} review #{statusModalConfig.item?.id} linked to Booking{" "}
              <strong>{statusModalConfig.item?.bookingCode}</strong>?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={handleCloseStatusModal}
                disabled={processingStatus}
                sx={{ borderColor: "#D1D5DB", color: "#374151", textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmStatusChange}
                disabled={processingStatus}
                sx={{
                  bgcolor: statusModalConfig.actionType === "verify" ? "#059669" : "#DC2626",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: statusModalConfig.actionType === "verify" ? "#047857" : "#B91C1C",
                  },
                }}
              >
                {processingStatus
                  ? "Processing..."
                  : statusModalConfig.actionType === "verify"
                  ? "Confirm Verification"
                  : "Confirm Rejection"}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Media Preview Modal */}
      {Boolean(previewMediaUrl) && (
        <Modal open={Boolean(previewMediaUrl)} onClose={() => setPreviewMediaUrl(null)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "85vw",
              maxHeight: "85vh",
              bgcolor: "white",
              borderRadius: "16px",
              boxShadow: 24,
              p: 3,
              outline: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontWeight: 600, color: "#111827", textTransform: "capitalize", fontFamily: FONT }}>
                {getMediaType(previewMediaUrl)} Preview
              </Typography>
              <CloseIcon onClick={() => setPreviewMediaUrl(null)} sx={{ cursor: "pointer", color: "#4B5563" }} />
            </Box>

            {getMediaType(previewMediaUrl) === "video" && (
              <Box
                component="video"
                controls
                autoPlay
                src={previewMediaUrl}
                sx={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px" }}
              />
            )}

            {getMediaType(previewMediaUrl) === "audio" && (
              <Box sx={{ p: 4, width: "100%", minWidth: "320px", display: "flex", justifyContent: "center" }}>
                <Box component="audio" controls autoPlay src={previewMediaUrl} sx={{ width: "100%" }} />
              </Box>
            )}

            {getMediaType(previewMediaUrl) === "pdf" && (
              <iframe
                src={previewMediaUrl}
                title="PDF Preview"
                style={{ width: "75vw", height: "70vh", border: "none", borderRadius: "8px" }}
              />
            )}

            {getMediaType(previewMediaUrl) === "image" && (
              <Box
                component="img"
                src={previewMediaUrl}
                alt="Attachment Preview"
                sx={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px", objectFit: "contain" }}
              />
            )}
          </Box>
        </Modal>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          p: 5,
          bgcolor: "#F9FAFB",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          my: 4,
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ color: "#111827", fontSize: 28, fontFamily: FONT, fontWeight: 700 }}>
              Customer Reviews
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: 14, fontFamily: FONT, mt: 0.5 }}>
              Showing {totalCount} reviews captured by fleet executives and customer bookings
            </Typography>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={handleExportCSV}
            sx={{
              px: 2.25,
              py: 1.5,
              bgcolor: "white",
              borderRadius: "8px",
              outline: "1px solid #D1D5DB",
              outlineOffset: "-1px",
              cursor: "pointer",
              "&:hover": { bgcolor: "#F9FAFB" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 16, color: "#374151" }} />
            <Typography sx={{ color: "#374151", fontSize: 14, fontFamily: FONT, fontWeight: 600 }}>
              Export CSV
            </Typography>
          </Stack>
        </Stack>

        {/* Filters */}
        <Stack
          direction="column"
          spacing={2}
          sx={{
            p: 2.5,
            bgcolor: "#F3F4F6",
            borderRadius: "16px",
            outline: "1px solid #E5E7EB",
            outlineOffset: "-1px",
          }}
        >
          <Stack direction="row" spacing={2} sx={{ width: "100%", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "180px" }}>
              <FilterPill
                label="Fleet Executive"
                value={selectedExecutiveName}
                options={executiveOptions}
                onChange={(e) => setInputExecutiveId(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: "180px" }}>
              <FilterPill
                label="Status"
                value={selectedStatusName}
                options={verificationStatusOptions}
                onChange={(e) => setInputVerificationStatus(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: "180px" }}>
              <TextField
                fullWidth
                size="small"
                label="Booking ID / Code"
                placeholder="Enter Booking ID"
                value={inputBookingId}
                onChange={(e) => setInputBookingId(e.target.value)}
                sx={{ bgcolor: "white", borderRadius: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>
            <Box sx={{ flex: 1.5, minWidth: "260px", display: "flex", alignItems: "center" }}>
              <DaterangePicker
                selectionRange={selectionRange}
                setSelectionRange={setSelectionRange}
                style="0"
                renderInput={({ onClick }) => (
                  <TextField
                    fullWidth
                    size="small"
                    label="Date Added (Range)"
                    value={
                      selectionRange?.startDate && selectionRange?.endDate
                        ? `${new Date(selectionRange.startDate).toLocaleDateString()} - ${new Date(selectionRange.endDate).toLocaleDateString()}`
                        : ""
                    }
                    onClick={onClick}
                    placeholder="Select Date Added Range"
                    InputProps={{ readOnly: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "& .MuiOutlinedInput-root": { borderRadius: "8px", cursor: "pointer" },
                    }}
                  />
                )}
              />
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleResetClick}
              sx={{
                borderColor: "#D43535",
                color: "#D43535",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: "#b52b2b", bgcolor: "#FFF8F8" },
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearchClick}
              sx={{
                bgcolor: "#34495e",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#2c3e50" },
              }}
            >
              Search
            </Button>
          </Stack>
        </Stack>

        {/* Stats Grid */}
        <Stack direction="row" spacing={2.5}>
          <StatCard
            label="Total Reviews"
            value={`${totalCount}`}
            badge="Overview"
            badgeColor="#2376FF"
            badgeBg="#EFF6FF"
            barColor="#2376FF"
          />
          <StatCard
            label="Verified"
            value={`${rows.filter((r) => r.isVerified && r.reviewStatus !== "Rejected").length}`}
            badge="Approved"
            badgeColor="#059669"
            badgeBg="#ECFDF5"
            barColor="#059669"
          />
          <StatCard
            label="Pending"
            value={`${rows.filter((r) => !r.isVerified && r.reviewStatus !== "Rejected").length}`}
            badge="Action Required"
            badgeColor="#D97706"
            badgeBg="#FFF7ED"
            barColor="#D97706"
          />
          <StatCard
            label="Rejected"
            value={`${rows.filter((r) => r.reviewStatus === "Rejected" || r.isRejected).length}`}
            badge="Declined"
            badgeColor="#DC2626"
            badgeBg="#FEF2F2"
            barColor="#DC2626"
          />
        </Stack>

        {/* Data Table */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: "24px",
            outline: "1px solid #E5E7EB",
            outlineOffset: "-1px",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2.5}
            sx={{ px: 3, py: 2.25, bgcolor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}
          >
            {columns.map((c) => (
              <Typography
                key={c.key}
                sx={{
                  flex: c.flex,
                  width: c.width,
                  textAlign: c.align || "left",
                  color: "#4B5563",
                  fontSize: 13,
                  fontFamily: FONT,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {c.label}
              </Typography>
            ))}
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography sx={{ color: "#6B7280", fontFamily: FONT, fontSize: 14 }}>
                No customer reviews found.
              </Typography>
            </Box>
          ) : (
            rows.map((r, i) => {
              const attachments = normalizeAttachments(r.reviewAttachment);
              const isRejected = r.reviewStatus === "Rejected" || r.isRejected;
              const isVerified = r.isVerified && !isRejected;

              return (
                <Stack
                  key={r.id || i}
                  direction="row"
                  alignItems="center"
                  spacing={2.5}
                  sx={{
                    px: 3,
                    py: 2.25,
                    borderBottom: i < rows.length - 1 ? "1px solid #F3F4F6" : "none",
                    "&:hover": { bgcolor: "#FAFAFA" },
                  }}
                >
                  {/* Executive Column */}
                  <Box sx={{ flex: 1.1 }}>
                    <Typography sx={{ color: "#111827", fontSize: 15, fontFamily: FONT, fontWeight: 600 }}>
                      {r.fleetExecutiveName?.trim() || "Unassigned Staff"}
                    </Typography>
                    <Stack direction="column" spacing={0.25} mt={0.25}>
                      <Typography sx={{ color: "#6B7280", fontSize: 13, fontFamily: FONT }}>
                        {r.fleetExecutiveEmail || `Customer ID: ${r.customerId}`}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          onClick={() => {
                            const navigate = `/viewallbookings?bookingid=${r?.bookingCode}&id=${r?.bookingId}&display=true`;
                            window.open(navigate, "_blank");
                          }}
                          sx={{
                            color: "#031b3b",
                            fontSize: 13,
                            fontFamily: FONT,
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                        >
                          {r.bookingCode ? `Booking: ${r.bookingCode}` : `Booking ID: ${r.bookingId}`}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Review Text */}
                  <Box sx={{ flex: 1.8 }}>
                    <Typography
                      sx={{
                        color: "#1F2937",
                        fontSize: 14,
                        fontFamily: FONT,
                        lineHeight: 1.45,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {r.textReview || "No written review text provided."}
                    </Typography>
                  </Box>

                  {/* Attachments */}
                  <Box sx={{ width: 130 }}>
                    {attachments.length > 0 ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        {attachments.map((att, attIdx) => {
                          const fileUrl = typeof att === "string" ? att : att?.url;
                          const fileName = typeof att === "object" ? att?.fileName : "attachment";
                          const isImg = getMediaType(fileUrl) === "image";

                          return (
                            <Box
                              key={attIdx}
                              onClick={() => setPreviewMediaUrl(fileUrl)}
                              sx={{
                                position: "relative",
                                width: 44,
                                height: 44,
                                borderRadius: "8px",
                                overflow: "hidden",
                                cursor: "pointer",
                                outline: "1px solid #E5E7EB",
                                bgcolor: "#F3F4F6",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "&:hover .overlay": { opacity: 1 },
                              }}
                            >
                              {isImg ? (
                                <Avatar variant="rounded" src={fileUrl} alt={fileName} sx={{ width: 44, height: 44 }}>
                                  <InsertPhotoIcon sx={{ color: "#9CA3AF" }} />
                                </Avatar>
                              ) : (
                                renderThumbnailIcon(fileUrl)
                              )}
                              <Box
                                className="overlay"
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  bgcolor: "rgba(0,0,0,0.4)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: 0,
                                  transition: "opacity 0.2s",
                                }}
                              >
                                <OpenInNewIcon sx={{ color: "white", fontSize: 16 }} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography sx={{ color: "#9CA3AF", fontSize: 13, fontFamily: FONT }}>
                        No media
                      </Typography>
                    )}
                  </Box>

                  {/* Status */}
                  <Box sx={{ width: 130 }}>
                    <StatusBadge row={r} />
                  </Box>

                  {/* Created At */}
                  <Box sx={{ width: 160 }}>
                    <Typography sx={{ color: "#111827", fontSize: 13, fontFamily: FONT }}>
                      {r.createdAt ? formatDateAndTime(r.createdAt) : "N/A"}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ width: 180, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                    {actionsToAccess?.includes("Approve Reject Customer Reviews") ? (
                      isVerified ? (
                        <Typography sx={{ fontSize: 12, color: "#059669", fontWeight: 600, fontFamily: FONT }}>
                          Verified
                        </Typography>
                      ) : isRejected ? (
                        <Typography sx={{ fontSize: 12, color: "#DC2626", fontWeight: 600, fontFamily: FONT }}>
                          Rejected
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenStatusModal(r, "verify")}
                            sx={{
                              bgcolor: "#059669",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: 12,
                              borderRadius: "6px",
                              "&:hover": { bgcolor: "#047857" },
                            }}
                          >
                            Verify
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenStatusModal(r, "reject")}
                            sx={{
                              borderColor: "#DC2626",
                              color: "#DC2626",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: 12,
                              borderRadius: "6px",
                              "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                            }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      )
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "#9CA3AF", fontFamily: FONT }}>
                        View Only
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })
          )}

          {/* Pagination Controls */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              borderTop: "1px solid #E5E7EB",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                fontFamily: FONT,
                fontSize: 13,
                color: "#6B7280",
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default CustomerReviews;